import { saleorAuthClient } from '@/graphql/client';
import type { CountryCode } from '@/graphql/saleor/api';
import { paths } from '@/lib/paths';
import { StorageHelper } from '@/lib/storage';
import { wishlistActions } from '@/states/wishlistState';
import type {
  LocalCountryWishlistMap,
  SkuWishlistMap,
  WishlistItem,
} from '@/views/WishListView/types';

const storageHelper = new StorageHelper();

/**
 * Fetch customer's wishlist by country from server and convert to local wishlist format
 * @param countryCode
 */
export const getServerWishlistByCountry = async (
  countryCode: CountryCode,
): Promise<SkuWishlistMap> => {
  try {
    const res = await saleorAuthClient
      .fetchWithAuth(
        `${paths.api.wishlist}/${countryCode}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        },
        { allowPassingTokenToThirdPartyDomains: true },
      )
      .then((res) => res.json());

    const wishlistItems = res?.data?.wishlists ?? [];

    return wishlistItems.reduce((acc: SkuWishlistMap, item: WishlistItem) => {
      acc[item.sku] = {
        sku: item.sku,
        type: item.type,
        price: item.price,
        theCurrency: item.theCurrency,
      };
      return acc;
    }, {} as Record<string, WishlistItem>);
  } catch (error) {
    return {};
  }
};

export const getLocalWishlistByCountry = (
  countryCode: CountryCode,
): SkuWishlistMap => {
  const countryWishlistMap = (storageHelper.getLocalStorage('wishList') ??
    {}) as LocalCountryWishlistMap;

  return countryWishlistMap[countryCode] ?? {};
};

export const updateLocalWishlistByCountry = (
  countryCode: CountryCode,
  countryWishlist: SkuWishlistMap,
) => {
  const prevCountryWishlistMap = (storageHelper.getLocalStorage('wishList') ??
    {}) as LocalCountryWishlistMap;
  const newCountryWishlistMap = {
    ...prevCountryWishlistMap,
    [countryCode]: countryWishlist,
  };
  storageHelper.setLocalStorage('wishList', newCountryWishlistMap);
  wishlistActions.updateIsHighlighted(countryCode);
};

/**
 * @return {boolean} Indicates whether adding the items to the wishlist was successful.
 */
export const addItemsToServerWishlistByCountry = async (
  countryCode: CountryCode,
  wishlistMap: SkuWishlistMap,
): Promise<boolean> => {
  if (Object.keys(wishlistMap).length <= 0) return false;

  const requestBody = {
    country: countryCode,
    wishlists: Object.values(wishlistMap).map((item) => ({
      ...item,
      price: item.price.toString(),
    })),
  };
  try {
    await saleorAuthClient
      .fetchWithAuth(
        paths.api.wishlist,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          body: JSON.stringify(requestBody),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      )
      .then((res) => res.json());
    return true;
  } catch (error) {
    return false;
  }
};
